import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { Button } from 'components/ui/button';

import { fetchKeyAccountHeader } from './api';
import TabBar from './components/TabBar';
import { ConversionType } from './components/UnitToggle';
import RevenueProfitabilityTab from './tabs/RevenueProfitabilityTab';
import UnbilledTab from './tabs/UnbilledTab';
import ReceivablesTab from './tabs/ReceivablesTab';

const TABS = [
    { key: 'revenue', label: 'Revenue & Profitability' },
    { key: 'unbilled', label: 'Unbilled' },
    { key: 'receivables', label: 'Accounts Receivable' },
];

type TabKey = 'revenue' | 'unbilled' | 'receivables';

const KeyAccountDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumbs();
    const { customerCode = '' } = useParams<{ customerCode: string }>();
    const [searchParams, setSearchParams] = useSearchParams();

    const tabParam = (searchParams.get('tab') as TabKey) || 'revenue';
    const tab: TabKey = TABS.some((t) => t.key === tabParam) ? tabParam : 'revenue';

    const [customerName, setCustomerName] = React.useState<string>(
        searchParams.get('customerName') || ''
    );
    const [headerError, setHeaderError] = React.useState<string>('');

    const [conversionType, setConversionType] = React.useState<ConversionType>('Lakhs');

    // keep-alive: mount a tab on first activation, then hide instead of unmounting
    const [activated, setActivated] = React.useState<Set<TabKey>>(() => new Set([tab]));

    React.useEffect(() => {
        setActivated((prev) => {
            if (prev.has(tab)) return prev;
            const next = new Set(prev);
            next.add(tab);
            return next;
        });
    }, [tab]);

    // validated header (also covers deep links without ?customerName=)
    React.useEffect(() => {
        if (!customerCode) return;
        fetchKeyAccountHeader(customerCode)
            .then((h) => {
                setCustomerName(h.customer_name);
            })
            .catch((err: unknown) => {
                const isForbidden = (err as any)?.status === 403;
                setHeaderError(
                    isForbidden
                        ? 'This customer is not one of your key accounts.'
                        : err instanceof Error
                            ? err.message
                            : 'Failed to load account'
                );
            });
    }, [customerCode]);

    React.useEffect(() => {
        setBreadcrumbs([
            { label: 'Reports', href: '/reports/pnl' },
            { label: 'Key Accounts', href: '/reports/key-accounts' },
            { label: customerName || customerCode },
        ]);
    }, [setBreadcrumbs, customerName, customerCode]);

    const handleTabChange = (key: string) => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', key);
        setSearchParams(next, { replace: true });
    };

    if (headerError) {
        return (
            <div className="space-y-6 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-4 sm:px-6">
                <div className="flex items-center gap-4">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/reports/key-accounts')}
                        className="flex items-center gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <h1 className="text-xl font-semibold text-gray-900">Key Account</h1>
                </div>
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600 text-sm">{headerError}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-4 sm:px-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/reports/key-accounts')}
                    className="flex items-center gap-2"
                >
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <h1 className="text-xl font-semibold text-gray-900 uppercase">
                    {customerName || customerCode}
                </h1>
            </div>

            <TabBar tabs={TABS} active={tab} onChange={handleTabChange} />

            {activated.has('revenue') && (
                <div className={tab === 'revenue' ? '' : 'hidden'}>
                    <RevenueProfitabilityTab
                        customerCode={customerCode}
                        conversionType={conversionType}
                        onConversionChange={setConversionType}
                    />
                </div>
            )}

            {activated.has('unbilled') && (
                <div className={tab === 'unbilled' ? '' : 'hidden'}>
                    <UnbilledTab
                        customerCode={customerCode}
                        customerName={customerName || customerCode}
                        conversionType={conversionType}
                        onConversionChange={setConversionType}
                    />
                </div>
            )}

            {activated.has('receivables') && (
                <div className={tab === 'receivables' ? '' : 'hidden'}>
                    <ReceivablesTab
                        customerCode={customerCode}
                        customerName={customerName || customerCode}
                        conversionType={conversionType}
                        onConversionChange={setConversionType}
                    />
                </div>
            )}
        </div>
    );
};

export default KeyAccountDetailPage;
